import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import type { UploadFile } from "antd";
import dayjs from "dayjs";
import { FileTextOutlined, ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ReportLongTable, ReportSection } from "../reports/ReportTab";
import { ObjectCardEmpty } from "../../components/layout/ObjectCard";
import "./CostInvoices.css";
import { useFeedback } from "../../utils/feedback";
import { defaultRecognitionMonth } from "../invoices/invoiceRecognitionMonth";
import { formatCurrency } from "../../utils/currency";
import { COMPANY_COST_CATEGORIES } from "./api/companyCostsApi";
import {
  fetchCompanyCostGroups,
  groupOptions,
  type CompanyCostGroup,
} from "./api/companyCostGroupsApi";
import {
  CostInvoice,
  InvoiceOverview,
  costInvoiceError,
  createCostInvoice,
  decideCostInvoice,
  downloadCostInvoice,
  updateCostInvoice,
  fetchCostInvoices,
  fetchInvoiceTargets,
  syncCostInvoices,
} from "./api/companyCostInvoicesApi";

const CostInvoices: React.FC = () => {
  const { t } = useTranslation(["reports", "fixedCosts", "common"]);
  const label = (key: string) => t(`reports:costInvoices.${key}`);
  const feedback = useFeedback();
  const [data, setData] = useState<InvoiceOverview | null>(null);
  const [status, setStatus] = useState("PENDING");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [editing, setEditing] = useState<CostInvoice | null>(null);
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState<CostInvoice | null>(null);
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [targets, setTargets] = useState<Array<{ id: string; label: string }>>(
    [],
  );
  const [originals, setOriginals] = useState<CostInvoice[]>([]);
  const [originalPage, setOriginalPage] = useState(0);
  const [moreOriginals, setMoreOriginals] = useState(false);
  const [createForm] = Form.useForm();
  const [decisionForm] = Form.useForm();
  const [syncForm] = Form.useForm();
  const decisionStatus = Form.useWatch("status", decisionForm);
  // Cost groups (MPK) for the included cost: the cost row is locked once
  // bound to the invoice, so its group is chosen here.
  const [costGroups, setCostGroups] = useState<CompanyCostGroup[]>([]);
  const targetType = Form.useWatch("targetType", decisionForm);
  const targetSearchSequence = useRef(0);
  const requestSequence = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const refresh = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    try {
      const next = await fetchCostInvoices(status, page * 50);
      if (mounted.current && sequence === requestSequence.current) {
        setData(next);
        setFailure(null);
      }
    } catch (e) {
      if (mounted.current && sequence === requestSequence.current)
        setFailure(costInvoiceError(e, t("reports:costInvoices.loadError")));
    } finally {
      if (mounted.current && sequence === requestSequence.current)
        setLoading(false);
    }
  }, [page, status, t]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const searchTargets = useCallback(
    async (search: string) => {
      const sequence = ++targetSearchSequence.current;
      if (!targetType) {
        setTargets([]);
        return;
      }
      try {
        const rows = await fetchInvoiceTargets(targetType, search);
        if (sequence === targetSearchSequence.current) setTargets(rows);
      } catch (e) {
        if (sequence === targetSearchSequence.current)
          setFailure(costInvoiceError(e, t("reports:costInvoices.loadError")));
      }
    },
    [targetType, t],
  );
  useEffect(() => {
    setTargets([]);
    decisionForm.setFieldValue("targetId", undefined);
    void searchTargets("");
  }, [targetType, decisionForm, searchTargets]);
  const loadOriginals = async (nextPage: number) => {
    try {
      const result = await fetchCostInvoices("INCLUDED", nextPage * 50);
      setOriginals((current) =>
        nextPage === 0 ? result.invoices : [...current, ...result.invoices],
      );
      setOriginalPage(nextPage);
      setMoreOriginals(result.hasMore);
    } catch (e) {
      setFailure(costInvoiceError(e, label("loadError")));
    }
  };
  const openDecision = (invoice: CostInvoice) => {
    setSelected(invoice);
    fetchCompanyCostGroups()
      .then((groups) => { if (mounted.current) setCostGroups(groups); })
      .catch(() => { if (mounted.current) setCostGroups([]); });
    decisionForm.resetFields();
    decisionForm.setFieldsValue({
      version: invoice.version,
      status: "INCLUDED",
      // Owner decision 2026-10-13: the month defaults to the invoice issue
      // date (a backdated cost lands in its own month), today without one.
      month: dayjs(`${defaultRecognitionMonth(invoice.issueDate)}-01`),
      category: "OTHER",
    });
    if (
      invoice.invoiceType.startsWith("Kor") ||
      invoice.invoiceType === "CORRECTION"
    )
      void loadOriginals(0);
  };
  const saveCreate = async () => {
    let values;
    try {
      values = await createForm.validateFields();
    } catch {
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...values,
        issueDate: values.issueDate.format("YYYY-MM-DD"),
      };
      const invoice = editing
        ? await updateCostInvoice(editing.id, {
            ...payload,
            version: editing.version,
          })
        : await createCostInvoice(payload, files[0]?.originFileObj);
      setCreating(false);
      setFiles([]);
      createForm.resetFields();
      feedback.success(
        t("reports:costInvoices.saved", { number: invoice.invoiceNumber }),
      );
      await refresh();
    } catch (e) {
      feedback.error(costInvoiceError(e, label("saveError")));
    } finally {
      setBusy(false);
    }
  };
  const saveDecision = async () => {
    let values;
    try {
      values = await decisionForm.validateFields();
    } catch {
      return;
    }
    if (!selected) return;
    setBusy(true);
    try {
      await decideCostInvoice(selected.id, {
        ...values,
        version: selected.version,
        month: values.month?.format("YYYY-MM"),
      });
      feedback.success(
        t("reports:costInvoices.decided", { number: selected.invoiceNumber }),
      );
      setSelected(null);
      await refresh();
    } catch (e) {
      feedback.error(costInvoiceError(e, label("saveError")));
    } finally {
      setBusy(false);
    }
  };
  const synchronize = async () => {
    let values;
    try {
      values = await syncForm.validateFields();
    } catch {
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      let result = await syncCostInvoices({
        from: values.range[0].format("YYYY-MM-DD"),
        to: values.range[1].format("YYYY-MM-DD"),
      });
      while (!result.done && mounted.current)
        result = await syncCostInvoices({ runId: result.runId });
      if (result.done)
        feedback.success(
          t("reports:costInvoices.synced", {
            count: result.imported,
            duplicates: result.duplicates,
          }),
        );
      setSyncing(false);
      await refresh();
    } catch (e) {
      await refresh();
      setFailure(costInvoiceError(e, label("syncError")));
      setSyncing(false);
    } finally {
      setBusy(false);
    }
  };
  const download = async (invoice: CostInvoice) => {
    setBusy(true);
    try {
      await downloadCostInvoice(invoice);
    } catch (e) {
      feedback.error(costInvoiceError(e, label("loadError")));
    } finally {
      setBusy(false);
    }
  };
  const required = [{ required: true, message: label("required") }];
  const correction = Boolean(
    selected &&
      (selected.invoiceType.startsWith("Kor") ||
        selected.invoiceType === "CORRECTION"),
  );
  const openCreate = () => {
    setEditing(null);
    createForm.resetFields();
    createForm.setFieldsValue({
      currency: "PLN",
      invoiceType: "STANDARD",
      issueDate: dayjs(),
    });
    setFiles([]);
    setCreating(true);
  };
  const openSync = () => {
    syncForm.setFieldsValue({
      range: [dayjs().startOf("month"), dayjs()],
    });
    setSyncing(true);
  };
  const canManage = Boolean(data?.canManage);
  const canSync = canManage && Boolean(data?.ksefReady);
  return (
    <div className="report-tab cost-invoices">
      <div className="report-controls">
        <Select
          aria-label={label("status")}
          value={status}
          className="cost-invoices__status"
          onChange={(value) => {
            setStatus(value);
            setPage(0);
          }}
          options={["", "PENDING", "INCLUDED", "LINKED", "OMITTED"].map(
            (value) => ({ value, label: label(`statuses.${value || "ALL"}`) }),
          )}
        />
        <Button
          icon={<ReloadOutlined />}
          disabled={busy}
          onClick={() => void refresh()}
        >
          {label("refresh")}
        </Button>
        <div className="report-controls__end">
          <span className="report-controls__counter">
            {label("lastSync")}:{" "}
            {data?.lastSync
              ? dayjs(data.lastSync.completedAt).format("DD.MM.YYYY HH:mm")
              : "—"}
          </span>
          {canManage && (
            <Button disabled={busy} onClick={openCreate}>
              {label("add")}
            </Button>
          )}
          {canSync && (
            <Button type="primary" disabled={busy} onClick={openSync}>
              {label("sync")}
            </Button>
          )}
        </div>
      </div>
      {failure && <Alert type="error" showIcon message={failure} />}
      {data && !data.ksefReady && (
        <Alert type="info" showIcon message={label("notConfigured")} />
      )}
      {data?.ksefReady && data.environment !== "production" && (
        <Alert type="warning" showIcon message={label("testEnvironment")} />
      )}
      <ReportSection
        icon={<FileTextOutlined />}
        tone="blue"
        title={t("reports:profitability.tabCostInvoices")}
        lead={label(canSync ? "description" : canManage ? "descriptionManual" : "descriptionReadOnly")}
      >
        <ReportLongTable rowCount={(data?.invoices || []).length} testId="cost-invoices-rows">
          <Table<CostInvoice>
            className="report-table"
            rowKey="id"
            loading={loading}
            dataSource={data?.invoices || []}
            pagination={false}
            scroll={{ x: 850 }}
            locale={{
              emptyText: (
                <ObjectCardEmpty
                  icon={<FileTextOutlined />}
                  text={
                    canManage
                      ? `${t("reports:costInvoices.empty", {
                          status: label(`statuses.${status || "ALL"}`),
                        })} ${label(canSync ? "emptyHintSync" : "emptyHintManual")}`
                      : t("reports:costInvoices.empty", {
                          status: label(`statuses.${status || "ALL"}`),
                        })
                  }
                />
              ),
            }}
            columns={[
              {
                title: label("invoice"),
                key: "invoice",
                render: (_, row) => (
                  <div className="cost-invoices__party">
                    <span className="cost-invoices__number report-cut" title={row.invoiceNumber}>{row.invoiceNumber}</span>
                    <span className="report-cut report-muted" title={[row.sellerName, row.sellerTaxId].filter(Boolean).join(" · ")}>
                      {[row.sellerName, row.sellerTaxId].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                ),
              },
              {
                title: label("issueDate"),
                dataIndex: "issueDate",
                render: (value) => (
                  <span className="report-num">{dayjs(value).format("DD.MM.YYYY")}</span>
                ),
              },
              {
                title: label("netAmount"),
                key: "amount",
                align: "right",
                render: (_, row) => (
                  <span className="report-num">{formatCurrency(row.netAmount, row.currency)}</span>
                ),
              },
              {
                title: label("source"),
                key: "source",
                render: (_, row) => (row.ksefNumber ? "KSeF" : label("outside")),
              },
              {
                title: label("status"),
                key: "status",
                render: (_, row) => {
                  const decision =
                    row.decision?.month ||
                    row.decision?.targetLabel ||
                    row.decision?.reason;
                  return (
                    <div className="cost-invoices__status-cell">
                      <Tag>{label(`statuses.${row.status}`)}</Tag>
                      {decision && <span className="report-cut report-muted" title={decision}>{decision}</span>}
                    </div>
                  );
                },
              },
              {
                title: label("actions"),
                key: "actions",
                align: "right",
                render: (_, row) => (
                  <div className="cost-invoices__actions">
                    {canManage && row.status === "PENDING" && (
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => openDecision(row)}
                      >
                        {label("review")}
                      </Button>
                    )}
                    {canManage &&
                      row.status === "PENDING" &&
                      !row.ksefNumber && (
                        <Button
                          size="small"
                          disabled={busy}
                          onClick={() => {
                            setEditing(row);
                            createForm.resetFields();
                            createForm.setFieldsValue({
                              ...row,
                              issueDate: dayjs(row.issueDate),
                            });
                            setCreating(true);
                          }}
                        >
                          {label("edit")}
                        </Button>
                      )}
                    {(row.hasFile || row.ksefNumber) && (
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => void download(row)}
                      >
                        {label("original")}
                      </Button>
                    )}
                  </div>
                ),
              },
            ]}
          />
        </ReportLongTable>
        {(page > 0 || data?.hasMore) && (
          <div className="report-pager">
            <Button
              disabled={page === 0 || loading}
              onClick={() => setPage(page - 1)}
            >
              {label("previous")}
            </Button>
            <span className="report-controls__counter">{page + 1}</span>
            <Button
              disabled={!data?.hasMore || loading}
              onClick={() => setPage(page + 1)}
            >
              {label("next")}
            </Button>
          </div>
        )}
      </ReportSection>
      <Modal
        title={label(editing ? "edit" : "add")}
        open={creating}
        onCancel={() => !busy && setCreating(false)}
        onOk={() => void saveCreate()}
        confirmLoading={busy}
        okText={label("saveDraft")}
        cancelText={label("cancel")}
      >
        <Form form={createForm} layout="vertical">
          {!editing && (
            <Upload
              accept=".pdf,.jpg,.jpeg,.png"
              maxCount={1}
              fileList={files}
              beforeUpload={() => false}
              onChange={({ fileList }) => setFiles(fileList)}
            >
              <Button>{label("attach")}</Button>
            </Upload>
          )}
          <Typography.Paragraph type="secondary">
            {label("manualHint")}
          </Typography.Paragraph>
          <Form.Item
            name="invoiceNumber"
            label={label("invoiceNumber")}
            rules={required}
          >
            <Input maxLength={256} />
          </Form.Item>
          <Form.Item
            name="sellerName"
            label={label("sellerName")}
            rules={required}
          >
            <Input maxLength={300} />
          </Form.Item>
          <Form.Item
            name="sellerTaxId"
            label={label("sellerTaxId")}
            rules={required}
          >
            <Input maxLength={40} />
          </Form.Item>
          <Form.Item
            name="issueDate"
            label={label("issueDate")}
            rules={required}
          >
            <DatePicker format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item
            name="invoiceType"
            label={label("invoiceType")}
            rules={required}
          >
            <Select
              options={["STANDARD", "CORRECTION"].map((value) => ({
                value,
                label: label(value),
              }))}
            />
          </Form.Item>
          <Form.Item name="currency" label={label("currency")} rules={required}>
            <Input maxLength={3} />
          </Form.Item>
          <Form.Item
            name="netAmount"
            label={label("netAmount")}
            rules={required}
          >
            <InputNumber
              precision={2}
              min={-1000000000}
              max={1000000000}
              className="cost-invoices__control"
            />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={`${label("review")}: ${selected?.invoiceNumber || ""}`}
        open={Boolean(selected)}
        onCancel={() => !busy && setSelected(null)}
        onOk={() => void saveDecision()}
        confirmLoading={busy}
        okText={label("approve")}
        cancelText={label("cancel")}
      >
        <Typography.Paragraph>
          {selected?.sellerName} ·{" "}
          {selected && formatCurrency(selected.netAmount, selected.currency)}
        </Typography.Paragraph>
        <Form form={decisionForm} layout="vertical">
          <Form.Item name="status" label={label("decision")} rules={required}>
            <Select
              options={["INCLUDED", "LINKED", "OMITTED"].map((value) => ({
                value,
                label: label(`decisions.${value}`),
              }))}
            />
          </Form.Item>
          {decisionStatus === "INCLUDED" && (
            <>
              <Form.Item name="month" label={label("month")} rules={required}>
                <DatePicker picker="month" format="MM.YYYY" />
              </Form.Item>
              <Form.Item
                name="category"
                label={label("category")}
                rules={required}
              >
                <Select
                  options={COMPANY_COST_CATEGORIES.map((value) => ({
                    value,
                    label: t(`fixedCosts:categories.${value}`),
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="groupId"
                label={label("group")}
                tooltip={t("fixedCosts:groups.invoiceFieldHint")}
              >
                <Select
                  allowClear
                  placeholder={t("fixedCosts:groups.none")}
                  options={groupOptions(costGroups).map((group) => ({
                    value: group.id,
                    label: group.name,
                  }))}
                  notFoundContent={t("fixedCosts:groups.noneYet")}
                />
              </Form.Item>
              {selected?.currency !== "PLN" && (
                <Form.Item
                  name="exchangeRate"
                  label={label("exchangeRate")}
                  rules={required}
                >
                  <InputNumber
                    min={0.000001}
                    max={1000000}
                    precision={6}
                    className="cost-invoices__control"
                  />
                </Form.Item>
              )}
              {correction && (
                <>
                  <Alert type="info" message={label("correctionHint")} />
                  <Form.Item
                    name="correctedInvoiceId"
                    label={label("correctedInvoice")}
                    rules={required}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      options={originals
                        .filter(
                          (row) => row.sellerTaxId === selected?.sellerTaxId,
                        )
                        .map((row) => ({
                          value: row.id,
                          label: `${row.invoiceNumber} · ${row.sellerName}`,
                        }))}
                    />
                  </Form.Item>
                  {moreOriginals && (
                    <Button
                      onClick={() => void loadOriginals(originalPage + 1)}
                    >
                      {label("loadMore")}
                    </Button>
                  )}
                </>
              )}
            </>
          )}
          {decisionStatus === "LINKED" && (
            <>
              <Form.Item
                name="targetType"
                label={label("targetType")}
                rules={required}
              >
                <Select
                  options={[
                    "COMPANY_COST",
                    "SUPPLIER_ORDER",
                    "ORDER",
                    "COST",
                  ].map((value) => ({
                    value,
                    label: label(`targets.${value}`),
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="targetId"
                label={label("target")}
                rules={required}
              >
                <Select
                  showSearch
                  filterOption={false}
                  onSearch={(value) => void searchTargets(value)}
                  options={targets.map((row) => ({
                    value: row.id,
                    label: row.label,
                  }))}
                />
              </Form.Item>
            </>
          )}
          <Form.Item
            name="reason"
            label={label("reason")}
            rules={decisionStatus === "OMITTED" ? required : []}
          >
            <Input.TextArea maxLength={2000} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={label("sync")}
        open={syncing}
        onCancel={() => !busy && setSyncing(false)}
        onOk={() => void synchronize()}
        confirmLoading={busy}
        okText={label("sync")}
        cancelText={label("cancel")}
      >
        <Typography.Paragraph>{label("syncHint")}</Typography.Paragraph>
        <Form form={syncForm} layout="vertical">
          <Form.Item name="range" label={label("range")} rules={required}>
            <DatePicker.RangePicker format="DD.MM.YYYY" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
export default CostInvoices;
